/**
 * Interpolation utility for Steve Render Service.
 *
 * Maps a numeric input value from one range to another, with support for:
 * - Multi-stop ranges (piecewise linear)
 * - Custom easing functions
 * - Extrapolation modes (extend, clamp, identity)
 *
 * Written from scratch for Steve.AI — standard linear interpolation math.
 */

/**
 * Interpolate a value from an input range to an output range.
 *
 * @param {number} input - The value to interpolate.
 * @param {number[]} inputRange - Strictly increasing array of input breakpoints.
 * @param {number[]} outputRange - Array of output values (same length as inputRange).
 * @param {object} [options]
 * @param {function} [options.easing] - Easing function applied to normalized progress.
 * @param {'extend'|'clamp'|'identity'} [options.extrapolateLeft='extend']
 * @param {'extend'|'clamp'|'identity'} [options.extrapolateRight='extend']
 * @returns {number}
 */
export function interpolate(input, inputRange, outputRange, options) {
  if (typeof input !== 'number') {
    throw new TypeError('interpolate() input must be a number, got: ' + typeof input);
  }
  if (!Array.isArray(inputRange) || inputRange.length < 2) {
    throw new Error('inputRange must be an array with at least 2 elements');
  }
  if (!Array.isArray(outputRange) || outputRange.length < 2) {
    throw new Error('outputRange must be an array with at least 2 elements');
  }
  if (inputRange.length !== outputRange.length) {
    throw new Error(
      `inputRange (${inputRange.length}) and outputRange (${outputRange.length}) must have the same length`
    );
  }

  // Validate monotonically increasing input range
  for (let i = 1; i < inputRange.length; i++) {
    if (inputRange[i] <= inputRange[i - 1]) {
      throw new Error(
        `inputRange must be strictly increasing. Got [${inputRange.join(',')}]`
      );
    }
  }

  const easing = options?.easing ?? ((t) => t);
  const extrapolateLeft = options?.extrapolateLeft ?? 'extend';
  const extrapolateRight = options?.extrapolateRight ?? 'extend';

  // Find which segment the input falls into
  let segmentIndex = 0;
  for (let i = 1; i < inputRange.length - 1; i++) {
    if (inputRange[i] <= input) {
      segmentIndex = i;
    }
  }

  const inMin = inputRange[segmentIndex];
  const inMax = inputRange[segmentIndex + 1];
  const outMin = outputRange[segmentIndex];
  const outMax = outputRange[segmentIndex + 1];

  let value = input;

  // Handle left extrapolation
  if (value < inputRange[0]) {
    if (extrapolateLeft === 'identity') return value;
    if (extrapolateLeft === 'clamp') value = inputRange[0];
    // 'extend' — continue with the math below
  }

  // Handle right extrapolation
  if (value > inputRange[inputRange.length - 1]) {
    if (extrapolateRight === 'identity') return value;
    if (extrapolateRight === 'clamp') value = inputRange[inputRange.length - 1];
    // 'extend' — continue with the math below
  }

  // If output range is flat in this segment, return the constant
  if (outMin === outMax) return outMin;

  // Normalize to [0, 1]
  let progress = (value - inMin) / (inMax - inMin);

  // Apply easing
  progress = easing(progress);

  // Map to output range
  return outMin + progress * (outMax - outMin);
}
